import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Check } from "lucide-react";
import { adminListQuestions, createQuestion, updateQuestion, toggleQuestion, deleteQuestion, reorderQuestions } from "../../riskprofiler/riskApi";

const blank = () => ({ question_text: "", option_a: "", option_b: "", option_c: "", option_d: "", active: true });
const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue";

const Editor = ({ initial, onClose, onSaved }) => {
    const [q, setQ] = useState(initial || blank());
    const [busy, setBusy] = useState(false);
    const isEdit = !!initial?.id;
    const save = async () => {
        if (q.question_text.trim().length < 3) { toast.error("Question text is required."); return; }
        for (const k of ["option_a", "option_b", "option_c", "option_d"]) {
            if (!q[k].trim()) { toast.error("All four options are required."); return; }
        }
        setBusy(true);
        try {
            const body = { question_text: q.question_text, option_a: q.option_a, option_b: q.option_b, option_c: q.option_c, option_d: q.option_d, active: q.active };
            if (isEdit) await updateQuestion(initial.id, body); else await createQuestion(body);
            toast.success(isEdit ? "Question updated." : "Question added.");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save question."); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="rp-question-editor">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-xl rounded-3xl bg-white p-6 md:p-8 shadow-2xl max-h-[88vh] overflow-y-auto thin-scroll">
                <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3"><X size={16} /></button>
                <h3 className="font-display text-xl font-semibold tracking-tight mb-5">{isEdit ? "Edit question" : "Add question"}</h3>
                <label className="block mb-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Question text</div>
                    <textarea className={`${inputCls} min-h-[80px]`} value={q.question_text} onChange={(e) => setQ({ ...q, question_text: e.target.value })} data-testid="rp-q-text" />
                </label>
                {["a", "b", "c", "d"].map((o, i) => (
                    <label key={o} className="block mb-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Option {o.toUpperCase()} <span className="text-oth-ink3 normal-case tracking-normal">(score {i + 1})</span></div>
                        <input className={inputCls} value={q[`option_${o}`]} onChange={(e) => setQ({ ...q, [`option_${o}`]: e.target.value })} data-testid={`rp-q-opt-${o}`} />
                    </label>
                ))}
                <label className="flex items-center gap-2 mt-4 mb-6 font-body text-sm text-oth-ink2">
                    <input type="checkbox" checked={q.active} onChange={(e) => setQ({ ...q, active: e.target.checked })} data-testid="rp-q-active" /> Active
                </label>
                <div className="flex items-center justify-end gap-2">
                    <button onClick={onClose} className="btn-pill btn-pill-light text-sm">Cancel</button>
                    <button onClick={save} disabled={busy} className="btn-pill btn-pill-blue text-sm disabled:opacity-60" data-testid="rp-q-save">{busy ? "Saving…" : "Save question"}</button>
                </div>
            </div>
        </div>
    );
};

export function RiskQuestionsTab() {
    const [questions, setQuestions] = useState([]);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = async () => { try { const d = await adminListQuestions(); setQuestions(d.questions); } catch { toast.error("Could not load questions."); } };
    useEffect(() => { load(); }, []);

    const move = async (idx, dir) => {
        const next = [...questions];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        setQuestions(next);
        try { await reorderQuestions(next.map((q) => q.id)); } catch { toast.error("Could not reorder."); load(); }
    };
    const toggle = async (q) => { try { await toggleQuestion(q.id, !q.active); load(); } catch { toast.error("Could not update."); } };
    const remove = async (q) => {
        if (!window.confirm(`Delete "${q.question_text.slice(0, 50)}…"? If it was used in any assessment, it will be deactivated instead to keep history intact.`)) return;
        try { const r = await deleteQuestion(q.id); toast.success(r.soft_deleted ? "Question deactivated (used in past assessments)." : "Question deleted."); load(); }
        catch { toast.error("Could not delete."); }
    };

    return (
        <div data-testid="rp-questions-tab">
            <div className="flex items-center justify-between mb-5">
                <p className="text-sm font-body text-oth-ink3">Scoring is fixed: A=1, B=2, C=3, D=4. Each question has equal weight.</p>
                <button onClick={() => setAdding(true)} className="btn-pill btn-pill-blue text-sm" data-testid="rp-add-question"><Plus size={14} /> Add question</button>
            </div>
            <div className="space-y-3">
                {questions.map((q, i) => (
                    <div key={q.id} className={`rounded-2xl border bg-white p-4 flex items-start gap-3 ${q.active ? "border-oth-line/70" : "border-dashed border-oth-line opacity-70"}`} data-testid={`rp-question-${i}`}>
                        <div className="flex flex-col gap-1 pt-0.5">
                            <button onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 disabled:opacity-30" data-testid={`rp-q-up-${i}`}><ArrowUp size={13} /></button>
                            <button onClick={() => move(i, 1)} disabled={i === questions.length - 1} className="w-7 h-7 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 disabled:opacity-30" data-testid={`rp-q-down-${i}`}><ArrowDown size={13} /></button>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-body text-sm font-medium text-oth-ink">{i + 1}. {q.question_text}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs font-body text-oth-ink3">
                                <span>A. {q.option_a}</span><span>B. {q.option_b}</span>
                                <span>C. {q.option_c}</span><span>D. {q.option_d}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={() => toggle(q)} title={q.active ? "Deactivate" : "Activate"} className={`px-2.5 h-8 rounded-lg border text-xs font-body flex items-center gap-1 ${q.active ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-oth-line text-oth-ink3"}`} data-testid={`rp-q-toggle-${i}`}>
                                {q.active ? <><Check size={12} /> Active</> : "Inactive"}
                            </button>
                            <button onClick={() => setEditing(q)} className="w-8 h-8 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 hover:bg-[#f0f0ec]" data-testid={`rp-q-edit-${i}`}><Pencil size={13} /></button>
                            <button onClick={() => remove(q)} className="w-8 h-8 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 hover:text-red-600 hover:border-red-200" data-testid={`rp-q-delete-${i}`}><Trash2 size={13} /></button>
                        </div>
                    </div>
                ))}
            </div>
            {adding && <Editor onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
            {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}
